import { motion } from "framer-motion";
import { MapPin, Clock, ArrowRight } from "lucide-react";
import { WHATSAPP_PALERMO, WHATSAPP_NUNEZ, IMG } from "./constants";

function LocationCard({ data, t, mapSrc, image, dataTestId, whatsapp }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      className="bg-bn-bg border border-bn-border overflow-hidden flex flex-col"
      data-testid={dataTestId}
    >
      <div className="relative h-56 overflow-hidden">
        <img src={image} alt={data.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <div className="absolute top-4 left-4 bg-bn-primary text-bn-bg font-display text-sm px-3 py-1 tracking-wider">
          {data.name}
        </div>
      </div>

      <div className="p-6 md:p-8 flex flex-col flex-1">
        <div className="space-y-5 flex-1">
          <div className="flex items-start gap-3">
            <MapPin className="text-bn-primary shrink-0 mt-1" size={20} />
            <div>
              <p className="font-body text-xs uppercase tracking-[0.2em] text-bn-text-muted mb-1">
                {t.locations.address}
              </p>
              <p className="font-body text-bn-secondary font-medium">{data.addr}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="text-bn-primary shrink-0 mt-1" size={20} />
            <div className="flex-1">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-bn-text-muted mb-2">
                {t.locations.hours}
              </p>
              <ul className="space-y-1.5">
                {data.hours.map((h) => (
                  <li
                    key={h.day}
                    className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3 text-sm"
                  >
                    <span className="font-body font-semibold text-bn-secondary min-w-[140px] shrink-0">
                      {h.day}
                    </span>
                    <span className="font-body text-bn-text-muted">{h.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <iframe
          src={mapSrc}
          width="100%"
          height="200"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title={data.name}
          className="mt-6 border border-bn-border grayscale-[20%]"
        />

        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <a
            href={`https://wa.me/${whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${dataTestId}-reserve`}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-bn-primary hover:bg-bn-primary-dark text-bn-bg px-5 py-3 font-body font-semibold text-sm uppercase tracking-wider transition-colors"
          >
            {t.locations.reserveHere}
            <ArrowRight size={16} />
          </a>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.addr)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${dataTestId}-directions`}
            className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-bn-secondary text-bn-secondary hover:bg-bn-secondary hover:text-bn-bg px-5 py-3 font-body font-semibold text-sm uppercase tracking-wider transition-colors"
          >
            {t.locations.directions}
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function Locations({ t }) {
  return (
    <section id="locations" data-testid="locations-section" className="relative py-24 md:py-32 bg-bn-bg">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center mb-12 md:mb-16">
          <p className="font-body text-bn-primary text-sm uppercase tracking-[0.3em] mb-4">
            — {t.locations.kicker}
          </p>
          <h2
            className="font-display text-bn-secondary text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-4"
            data-testid="locations-title"
          >
            {t.locations.title}
          </h2>
          <p className="font-body text-bn-text-muted text-base md:text-lg max-w-xl mx-auto">
            {t.locations.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
          <LocationCard
            data={t.locations.palermo}
            t={t}
            image={IMG.facadePalermo}
            whatsapp={WHATSAPP_PALERMO}
            dataTestId="location-palermo"
            mapSrc="https://www.google.com/maps?q=Jos%C3%A9%20A.%20Cabrera%204667%2C%20CABA&output=embed"
          />
          <LocationCard
            data={t.locations.nunez}
            t={t}
            image={IMG.facadeNunez}
            whatsapp={WHATSAPP_NUNEZ}
            dataTestId="location-nunez"
            mapSrc="https://www.google.com/maps?q=Arribe%C3%B1os%203198%2C%20CABA&output=embed"
          />
        </div>
      </div>
    </section>
  );
}
